/**
 * Fee receipt rows – built ONLY from what the system stores (no guessing):
 *  - payment_breakdown (fee_deposit_line_items): fee paid per installment by each receipt
 *  - receipt.late_fee: late fee of THIS receipt (never guessed from the amount)
 *  - installment.installment_discount: discount of each installment (preset + manual)
 *
 * Per installment:
 *   gross      = heads + transport + previous due
 *   priorPaid  = fee paid on it by earlier, not-cancelled receipts
 *   thisPaid   = fee paid on it by this receipt
 *   every receipt shows only ITS OWN discount (line item discount):
 *   Due = head - earlier receipts' discount - earlier payments, Concession = this receipt's discount,
 *   Paid = this receipt. A later receipt never changes what an earlier receipt shows.
 *   (old data without per-receipt discount: installment discount is shown)
 *
 * Old receipts without line items fall back to a waterfall of (amount - late_fee) over their installments.
 */
const n = (v) => {
  if (typeof v === "string") return parseFloat(v.replace(/,/g, "")) || 0;
  return parseFloat(v) || 0;
};

const isTuition = (h) => (h.headname || "").toLowerCase().includes("tuition");

/** fee paid on each installment by receipt r  -> Map(instId -> amount) */
export function paidByReceipt(r) {
  const m = new Map();
  const breakdown = r.payment_breakdown || [];
  if (breakdown.length) {
    breakdown.forEach((pb) => m.set(pb.installment_id, (m.get(pb.installment_id) || 0) + n(pb.amount)));
    return m;
  }
  // old receipts: waterfall of the fee part over its installments (in order)
  let left = Math.max(0, n(r.amount) - receiptLateFee(r));
  const insts = [...(r.installments || [])].sort((a, b) => n(a.orderno) - n(b.orderno));
  insts.forEach((i, idx) => {
    const due = Math.max(
      0,
      n(i.installment_amount ?? i.amount) + n(i.transport_fee) + n(i.installment_due_amount ?? i.previous_due) - n(i.installment_discount)
    );
    const pay = idx === insts.length - 1 ? left : Math.min(left, due);
    left -= pay;
    m.set(i.id, pay);
  });
  return m;
}

/** discount given by receipt r on each installment -> Map, or null when the receipt has no per-receipt discount data */
export function discountByReceipt(r) {
  const breakdown = r.payment_breakdown || [];
  if (!breakdown.length || breakdown.every((pb) => pb.discount === undefined || pb.discount === null)) return null;
  const m = new Map();
  breakdown.forEach((pb) => m.set(pb.installment_id, (m.get(pb.installment_id) || 0) + n(pb.discount)));
  return m;
}

/** split `amount` over rows (in the given order) up to each row's capacity */
function fill(rows, amount, capKey, outKey) {
  let left = Math.round(amount);
  rows.forEach((r) => {
    const take = Math.min(Math.max(0, r[capKey]), left);
    r[outKey] = (r[outKey] || 0) + take;
    r[capKey] -= take;
    left -= take;
  });
  return left;
}

/**
 * @param current      this receipt (with installments[] + heads, payment_breakdown, late_fee, amount)
 * @param priorReceipts earlier, not-cancelled receipts of the student (same session)
 * @returns { rows, totals }
 */
/**
 * Late fee of THIS receipt, made consistent with the fee table:
 *  1. receipt has line items -> late fee = amount - fee paid (line items)   (exact, same as backend)
 *  2. no line items          -> receipt.late_fee, but never more than the late fee the
 *                               installments actually show as paid (stored_late_fee_paid)
 */
export function receiptLateFee(current) {
  const amount = Math.round(n(current.amount));
  const breakdown = current.payment_breakdown || [];
  if (breakdown.length) {
    const fee = breakdown.reduce((s, pb) => s + n(pb.amount), 0);
    return Math.max(0, Math.round(amount - fee));
  }
  let late = Math.max(0, Math.round(n(current.late_fee)));
  const insts = current.installments || [];
  if (insts.length && insts.every((i) => i.stored_late_fee_paid !== undefined)) {
    const storedLate = insts.reduce((s, i) => s + n(i.stored_late_fee_paid), 0);
    late = Math.min(late, Math.round(storedLate));
  }
  return Math.min(late, amount);
}

export function buildReceiptRows(current, priorReceipts) {
  const lateFee = receiptLateFee(current);
  const amount = Math.round(n(current.amount));
  const thisPaid = paidByReceipt(current);

  const priorPaid = new Map();
  const priorDisc = new Map();
  (priorReceipts || []).forEach((r) => {
    paidByReceipt(r).forEach((v, k) => priorPaid.set(k, (priorPaid.get(k) || 0) + v));
    const d = discountByReceipt(r);
    if (d) d.forEach((v, k) => priorDisc.set(k, (priorDisc.get(k) || 0) + v));
  });
  // per-receipt discount (each receipt keeps its own discount); null = old data, use installment discount
  const thisDiscMap = discountByReceipt(current);

  // concessions that belong to one specific head (count how many heads carry each concession)
  const concessionCount = {};
  (current.installments || []).forEach((inst) =>
    (inst.heads || []).forEach((h) =>
      (h.concession || h.discount || []).forEach((c) => {
        const k = c.discountid || c.discount_name;
        if (k) concessionCount[k] = (concessionCount[k] || 0) + 1;
      })
    )
  );

  const agg = new Map(); // headname -> row
  const add = (name, month, due, disc, paid, order) => {
    if (due <= 0 && paid <= 0) return;
    const row = agg.get(name) || { headname: name, month: [], head_amount: 0, displayDiscount: 0, paidInThisReceipt: 0, order };
    row.head_amount += due;
    row.displayDiscount += disc;
    row.paidInThisReceipt += paid;
    if (month && !row.month.includes(month)) row.month.push(month);
    agg.set(name, row);
  };

  (current.installments || []).forEach((inst) => {
    // 1. line up everything this installment charges
    // no fee heads known -> one "Fee" row for the installment amount
    const headList = (inst.heads || []).length
      ? inst.heads
      : [{ headname: "Fee", head_amount: n(inst.installment_amount ?? inst.amount) }];
    const items = headList.map((h) => ({
      name: h.headname,
      gross: Math.round(n(h.head_amount)),
      specific: (h.concession || h.discount || []).reduce((s, c) => {
        const k = c.discountid || c.discount_name;
        return k && concessionCount[k] === 1 ? s + n(c.concession_value || c.discount_amount) : s;
      }, 0),
      tuition: isTuition(h),
      order: 1,
    }));
    if (n(inst.transport_fee) > 0) items.push({ name: "Transport Fee", gross: Math.round(n(inst.transport_fee)), specific: 0, order: 2 });
    const prevDue = n(inst.installment_due_amount ?? inst.previous_due);
    if (prevDue > 0) items.push({ name: "Previous Due", gross: Math.round(prevDue), specific: 0, order: 0 });

    // priority for discount and payment: previous due, tuition, other heads, transport
    items.sort((a, b) => a.order - b.order || (b.tuition ? 1 : 0) - (a.tuition ? 1 : 0));
    items.forEach((it) => { it.left = it.gross; });

    // discount split: earlier receipts' discount (hidden, reduces Due) and THIS receipt's discount (Concession)
    const instDisc = Math.round(n(inst.installment_discount));
    const earlierDisc = thisDiscMap ? Math.round(priorDisc.get(inst.id) || 0) : 0;
    const currentDisc = thisDiscMap ? Math.round(thisDiscMap.get(inst.id) || 0) : instDisc;

    // helper: apply a discount amount – head-specific concessions first, the rest tuition-first
    const applyDiscount = (amount, key) => {
      let left = Math.max(0, amount);
      items.forEach((it) => {
        const take = Math.min(it.left, Math.max(0, Math.round(it.specific) - (it.usedSpecific || 0)), left);
        it[key] = (it[key] || 0) + take; it.left -= take; it.usedSpecific = (it.usedSpecific || 0) + take; left -= take;
      });
      items.forEach((it) => {
        const take = Math.min(it.left, left);
        it[key] = (it[key] || 0) + take; it.left -= take; left -= take;
      });
    };

    // 2. earlier receipts: their discount, then their payment
    applyDiscount(earlierDisc, "priorDisc");
    const prior = Math.round(priorPaid.get(inst.id) || 0);
    items.forEach((it) => { it.prior = 0; });
    fill(items, prior, "left", "prior");

    // 3. this receipt: its discount, then its payment
    applyDiscount(currentDisc, "disc");
    items.forEach((it) => { it.paid = 0; });
    const leftover = fill(items, Math.round(thisPaid.get(inst.id) || 0), "left", "paid");
    if (leftover > 0 && items.length) items[items.length - 1].paid += leftover; // overpayment stays visible

    // 4. rows: Due = head - earlier discount - earlier payments, Concession = THIS receipt's discount
    items.forEach((it) => {
      add(it.name, inst.month, Math.max(0, it.gross - (it.priorDisc || 0) - it.prior), it.disc || 0, it.paid, it.order);
    });
  });

  const rows = Array.from(agg.values()).sort((a, b) => a.order - b.order);
  if (lateFee > 0) {
    rows.push({ headname: "Late Fee", month: (current.installments || []).map((i) => i.month).slice(-1), head_amount: lateFee, displayDiscount: 0, paidInThisReceipt: lateFee });
  }

  const totalDue = rows.reduce((s, r) => s + r.head_amount, 0);
  const totalConcession = rows.reduce((s, r) => s + r.displayDiscount, 0);
  const net = Math.max(0, totalDue - totalConcession);
  return {
    rows,
    totals: {
      totalDue,
      totalConcession,
      net,
      currentPaid: amount,
      balance: Math.max(0, net - amount),
    },
  };
}
